import { useState, useEffect, useCallback } from 'react';
import { reinitMetaPixel, isMetaPixelActive, isPixelInitialized, trackPixelEvent } from '@/lib/metaPixel';

interface Props {
  onToast: (msg: string, type: 'success' | 'error' | 'info') => void;
}

export default function MetaPixelPanel({ onToast }: Props) {
  const [pixelId, setPixelId] = useState(() => localStorage.getItem('meta_pixel_id') || '');
  const [enabled, setEnabled] = useState(() => localStorage.getItem('meta_pixel_enabled') === 'true');
  const [saved, setSaved] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [testSent, setTestSent] = useState(false);

  // Check status with polling for fbq readiness
  const checkStatus = useCallback(() => {
    const active = isMetaPixelActive();
    const initialized = isPixelInitialized();
    setIsActive(active);
    setIsInitializing(initialized && !active); // Initializing if pixel set up but fbq not ready yet
    return active;
  }, []);

  useEffect(() => {
    checkStatus();
    // Poll for status changes (fbq loads async)
    const interval = setInterval(checkStatus, 500);
    return () => clearInterval(interval);
  }, [checkStatus, saved]);

  const handleSave = () => {
    localStorage.setItem('meta_pixel_id', pixelId.trim());
    localStorage.setItem('meta_pixel_enabled', enabled.toString());
    reinitMetaPixel();
    setSaved(true);
    setIsActive(isMetaPixelActive());
    onToast(enabled && pixelId.trim() ? 'Meta Pixel active ho gaya!' : 'Meta Pixel settings save ho gayi!', 'success');
    setTimeout(() => setSaved(false), 3000);
  };

  const handleTestEvent = async () => {
    if (!isPixelInitialized()) {
      onToast('Pehle Pixel ID daalo aur Save karo!', 'error');
      return;
    }
    
    if (isInitializing) {
      onToast('Pixel initialize ho raha hai... 2-3 second wait karo!', 'info');
      return;
    }
    
    if (!isActive) {
      onToast('Pixel abhi ready nahi hai, page refresh karo!', 'error');
      return;
    }
    
    const success = await trackPixelEvent('Purchase', {
      content_ids: ['TEST_PRODUCT_001'],
      content_type: 'product',
      value: 999,
      currency: 'INR',
    });
    
    if (success) {
      setTestSent(true);
      onToast('Test Purchase event bheja gaya! Meta Events Manager mein check karo.', 'success');
      setTimeout(() => setTestSent(false), 4000);
    } else {
      onToast('Event bhejne mein error aaya, page refresh karke try karo!', 'error');
    }
  };

  const TRACKED_EVENTS = [
    { icon: 'ri-eye-line', name: 'PageView', desc: 'Har page load pe automatically', color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100' },
    { icon: 'ri-shopping-bag-line', name: 'ViewContent', desc: 'Product detail open hone pe', color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-100' },
    { icon: 'ri-shopping-cart-line', name: 'AddToCart', desc: 'Cart mein product add hone pe', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-100' },
    { icon: 'ri-secure-payment-line', name: 'InitiateCheckout', desc: 'Order confirm start hone pe', color: 'text-rose-600', bg: 'bg-rose-50', border: 'border-rose-100' },
    { icon: 'ri-money-rupee-circle-line', name: 'Purchase', desc: 'Order successfully place hone pe', color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-100' },
    { icon: 'ri-search-line', name: 'Search', desc: 'Customer kuch search kare tab', color: 'text-cyan-600', bg: 'bg-cyan-50', border: 'border-cyan-100' },
    { icon: 'ri-heart-line', name: 'AddToWishlist', desc: 'Product favourite karne pe', color: 'text-pink-600', bg: 'bg-pink-50', border: 'border-pink-100' },
    { icon: 'ri-user-add-line', name: 'CompleteRegistration', desc: 'Customer register kare tab', color: 'text-violet-600', bg: 'bg-violet-50', border: 'border-violet-100' },
  ];

  const SETUP_STEPS = [
    { step: '1', text: 'Facebook Business Manager mein jaao', link: 'https://business.facebook.com', linkText: 'business.facebook.com' },
    { step: '2', text: 'Events Manager → Data Sources → Pixels pe jaao' },
    { step: '3', text: 'Apna Pixel ID copy karo (15-digit number)' },
    { step: '4', text: 'Yahan paste karo, Enable toggle ON karo, Save karo' },
    { step: '5', text: 'Test Event bhejo aur Events Manager mein verify karo' },
  ];

  return (
    <div className="space-y-5">

      {/* Header Card */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/25">
            <i className="ri-facebook-circle-fill text-white text-2xl"></i>
          </div>
          <div className="flex-1">
            <h2 className="text-gray-900 font-bold text-lg">Meta Pixel Tracking</h2>
            <p className="text-gray-400 text-sm mt-0.5">Facebook &amp; Instagram ads ka performance track karo</p>
          </div>
          {/* Live Status Badge */}
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold ${
            isActive 
              ? 'bg-green-100 text-green-700 border border-green-200' 
              : isInitializing 
                ? 'bg-amber-100 text-amber-700 border border-amber-200' 
                : 'bg-gray-100 text-gray-500 border border-gray-200'
          }`}>
            <span className={`w-2 h-2 rounded-full ${
              isActive 
                ? 'bg-green-500 animate-pulse' 
                : isInitializing 
                  ? 'bg-amber-500 animate-pulse' 
                  : 'bg-gray-400'
            }}`}></span>
            {isActive ? 'Active' : isInitializing ? 'Initializing...' : 'Inactive'}
          </div>
        </div>
      </div>

      {/* Pixel ID Input Card */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
            <i className="ri-barcode-fill text-blue-600"></i>
          </div>
          <div>
            <p className="text-gray-900 font-bold text-sm">Pixel Configuration</p>
            <p className="text-gray-400 text-xs">Apna Meta Pixel ID yahan daalo</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Pixel ID Field */}
          <div>
            <label className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-wide">
              Meta Pixel ID
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center">
                <i className="ri-facebook-circle-fill text-blue-500 text-base"></i>
              </div>
              <input
                type="text"
                value={pixelId}
                onChange={e => setPixelId(e.target.value.replace(/\D/g, '').slice(0, 20))}
                placeholder="123456789012345"
                maxLength={20}
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 text-sm font-mono focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
              />
              {pixelId && (
                <button
                  onClick={() => setPixelId('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-gray-200 hover:bg-red-100 hover:text-red-500 flex items-center justify-center text-gray-400 transition-all cursor-pointer"
                >
                  <i className="ri-close-line text-xs"></i>
                </button>
              )}
            </div>
            <p className="text-gray-400 text-xs mt-1.5 flex items-center gap-1">
              <i className="ri-information-line text-gray-400"></i>
              Facebook Business Manager → Events Manager → Pixels se copy karo
            </p>
          </div>

          {/* Enable Toggle */}
          <div className={`rounded-2xl border p-4 transition-all ${enabled ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${enabled ? 'bg-blue-500' : 'bg-gray-300'}`}>
                  <i className={`ri-radar-line text-white text-lg`}></i>
                </div>
                <div>
                  <p className="text-gray-900 font-bold text-sm">Pixel Tracking</p>
                  <p className={`text-xs transition-all ${enabled ? 'text-blue-600' : 'text-gray-400'}`}>
                    {enabled ? 'Events track ho rahe hain' : 'Tracking band hai'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setEnabled(p => !p)}
                className={`relative w-12 h-6 rounded-full transition-all cursor-pointer shrink-0 ${enabled ? 'bg-blue-500' : 'bg-gray-300'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow ${enabled ? 'translate-x-6' : 'translate-x-0'}`}></span>
              </button>
            </div>
          </div>

          {/* Save Button */}
          {saved ? (
            <div className="w-full py-3.5 rounded-xl bg-green-100 border border-green-300 text-green-700 font-bold text-sm flex items-center justify-center gap-2">
              <i className="ri-checkbox-circle-fill text-green-500 text-base"></i>
              Saved! Pixel {enabled && pixelId ? 'Active' : 'Disabled'}
            </div>
          ) : (
            <button
              onClick={handleSave}
              disabled={!pixelId.trim() && enabled}
              className="w-full py-3.5 rounded-xl bg-gray-900 text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className="ri-save-line text-base"></i>
              Save Pixel Settings
            </button>
          )}

          {/* Test Event Button */}
          {enabled && pixelId && (
            <button
              onClick={handleTestEvent}
              className={`w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all cursor-pointer whitespace-nowrap border ${
                testSent
                  ? 'bg-green-50 text-green-600 border-green-200'
                  : 'bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100'
              }`}
            >
              <i className={`${testSent ? 'ri-checkbox-circle-line' : 'ri-test-tube-line'} text-base`}></i>
              {testSent ? 'Test Event Bheja Gaya!' : 'Send Test Event'}
            </button>
          )}
        </div>
      </div>

      {/* Tracked Events Grid */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center">
            <i className="ri-pulse-line text-emerald-600"></i>
          </div>
          <div>
            <p className="text-gray-900 font-bold text-sm">Tracked Events</p>
            <p className="text-gray-400 text-xs">Yeh sab events automatically Meta ko bheje jaate hain</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {TRACKED_EVENTS.map(event => (
            <div
              key={event.name}
              className={`flex items-center gap-3 p-3 rounded-xl border ${event.bg} ${event.border}`}
            >
              <div className={`w-9 h-9 rounded-lg bg-white flex items-center justify-center shrink-0 shadow-sm`}>
                <i className={`${event.icon} ${event.color} text-base`}></i>
              </div>
              <div className="min-w-0">
                <p className={`font-bold text-sm ${event.color}`}>{event.name}</p>
                <p className="text-gray-500 text-xs leading-tight">{event.desc}</p>
              </div>
              <div className="shrink-0 ml-auto">
                <i className="ri-check-line text-green-500 text-sm"></i>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Setup Guide */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center">
            <i className="ri-guide-line text-amber-600"></i>
          </div>
          <div>
            <p className="text-gray-900 font-bold text-sm">Setup Guide</p>
            <p className="text-gray-400 text-xs">Step-by-step Pixel setup karo</p>
          </div>
        </div>

        <div className="space-y-3">
          {SETUP_STEPS.map(s => (
            <div key={s.step} className="flex items-start gap-3">
              <div className="w-7 h-7 rounded-full bg-gray-900 text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                {s.step}
              </div>
              <div className="flex-1">
                <p className="text-gray-700 text-sm">{s.text}</p>
                {s.link && (
                  <a
                    href={s.link}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="text-blue-500 text-xs hover:underline flex items-center gap-1 mt-0.5"
                  >
                    <i className="ri-external-link-line"></i>
                    {s.linkText}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Benefits Card */}
      <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-5 text-white">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
            <i className="ri-bar-chart-grouped-line text-white text-lg"></i>
          </div>
          <div>
            <p className="font-bold text-base">Meta Pixel Se Kya Milega?</p>
            <p className="text-white/70 text-xs">Facebook &amp; Instagram ads ka ROI badhao</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { icon: 'ri-focus-3-line', text: 'Retargeting — jo log aaye unhe dobara ads dikhao' },
            { icon: 'ri-user-search-line', text: 'Lookalike Audience — similar customers dhundo' },
            { icon: 'ri-line-chart-line', text: 'Conversion Tracking — ads ka actual ROI dekho' },
            { icon: 'ri-price-tag-3-line', text: 'Dynamic Ads — products automatically promote karo' },
          ].map((b, i) => (
            <div key={i} className="flex items-start gap-2.5 bg-white/10 rounded-xl p-3">
              <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
                <i className={`${b.icon} text-white text-sm`}></i>
              </div>
              <p className="text-white/90 text-xs leading-relaxed">{b.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Warning */}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
        <div className="w-8 h-8 flex items-center justify-center shrink-0">
          <i className="ri-lightbulb-flash-line text-amber-500 text-lg"></i>
        </div>
        <div>
          <p className="text-amber-800 font-bold text-sm">Pro Tip</p>
          <p className="text-amber-700 text-xs mt-0.5 leading-relaxed">
            Pixel save karne ke baad <strong>page refresh karo</strong> — tab tracking shuru hogi. 
            Meta Events Manager mein <strong>Test Events</strong> tab se verify karo ki events aa rahe hain ya nahi.
          </p>
        </div>
      </div>
    </div>
  );
}
